import React from 'react';
import PropTypes from 'prop-types';

import prepareProviderLink from '../../../../helpers/general';

const EMPTY_VALUE = '—';

const getValue = (value) => (value === undefined || value === null || value === '' ? EMPTY_VALUE : value);

export const getTrackingNumbers = (tracking) => (
  tracking?.split(',').map((number) => number.trim()).filter(Boolean) || []
);

const DetailItem = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="mb-[4px] text-[11px] font-semibold uppercase tracking-wide text-romanSilver">
      {label}
    </dt>
    <dd className="break-words text-[13px] font-medium text-dark dark:text-secondary">
      {getValue(value)}
    </dd>
  </div>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DetailItem.defaultProps = {
  value: undefined,
};

const OrderedPartDetailsRow = ({ orderedPart }) => {
  const { _id: orderedPartId } = orderedPart;
  const trackingNumbers = getTrackingNumbers(orderedPart.ordered_part_tracking);

  return (
    <tr
      id={`ordered-part-details-${orderedPartId}`}
      data-detail-row="true"
      data-testid={`ordered-part-details-${orderedPartId}`}
    >
      <td colSpan={100} className="bg-input-bg/40 px-[18px] py-[14px] dark:bg-[#1b263b]">
        <dl className="grid max-w-[960px] grid-cols-1 gap-x-[20px] gap-y-[12px] sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Ordered type" value={orderedPart.ordered_type} />
          <DetailItem label="Part status" value={orderedPart.ordered_part_status} />
          <DetailItem label="PO" value={orderedPart.ordered_part_po} />
          <DetailItem label="Vendor" value={orderedPart.ordered_part_vendor} />
          <DetailItem label="Part MPN" value={orderedPart.ordered_part_mpn} />
          <DetailItem label="Brand" value={orderedPart.ordered_part_brand} />
          <DetailItem label="Part type" value={orderedPart.ordered_part_type} />
          <DetailItem label="Included" value={orderedPart.ordered_part_included} />
          <DetailItem label="Agent" value={orderedPart.ordered_part_agent} />
          <DetailItem label="Quantity ordered" value={orderedPart.ordered_part_qty_in_order} />
          <DetailItem label="Quantity delivered" value={orderedPart.ordered_part_qty_delivered} />
          <DetailItem label="Quantity returned" value={orderedPart.ordered_part_qty_return} />
          <DetailItem label="Price paid" value={orderedPart.ordered_part_price_paid} />
          <DetailItem label="Item price" value={orderedPart.ordered_part_price_in_order} />
          <DetailItem label="Order date" value={orderedPart.ordered_part_order_date} />
          <DetailItem label="Estimated delivery" value={orderedPart.ordered_part_est_delivery_date} />
          <DetailItem label="Delivered date" value={orderedPart.ordered_part_delivered_date} />
          <div className="min-w-0 sm:col-span-2 lg:col-span-3">
            <dt className="mb-[4px] text-[11px] font-semibold uppercase tracking-wide text-romanSilver">
              Tracking
            </dt>
            <dd className="flex flex-wrap gap-x-[14px] gap-y-[5px] text-[13px] font-medium">
              {trackingNumbers.length ? trackingNumbers.map((trackingNumber) => (
                <a
                  key={trackingNumber}
                  href={prepareProviderLink(trackingNumber)}
                  className="globalLink"
                  target="_blank"
                  rel="noreferrer"
                >
                  {trackingNumber}
                </a>
              )) : EMPTY_VALUE}
            </dd>
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-3">
            <DetailItem label="Note" value={orderedPart.ordered_part_note} />
          </div>
        </dl>
      </td>
    </tr>
  );
};

OrderedPartDetailsRow.propTypes = {
  orderedPart: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    ordered_type: PropTypes.string,
    ordered_part_status: PropTypes.string,
    ordered_part_po: PropTypes.string,
    ordered_part_vendor: PropTypes.string,
    ordered_part_mpn: PropTypes.string,
    ordered_part_brand: PropTypes.string,
    ordered_part_type: PropTypes.string,
    ordered_part_included: PropTypes.string,
    ordered_part_agent: PropTypes.string,
    ordered_part_qty_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_qty_delivered: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_qty_return: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_price_paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_price_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_order_date: PropTypes.string,
    ordered_part_est_delivery_date: PropTypes.string,
    ordered_part_delivered_date: PropTypes.string,
    ordered_part_tracking: PropTypes.string,
    ordered_part_note: PropTypes.string,
  }).isRequired,
};

export default OrderedPartDetailsRow;
